import type React from 'react';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { CivilGridFile, User } from '~/common/types';
import store from '~/store';
import Avatar from '../Avatar';
import MessageDate from './MessageDate';
import MessageFile from './MessageFile';

interface MessageProps {
  id?: string;
  createdBy: User;
  createdAt: Date;
  files?: CivilGridFile[];
  children?: React.ReactNode;
  onDownload?: (file: CivilGridFile) => void;
  /** The attached file whose download is waiting on its signed URL. */
  pendingFileId?: string;
}

export default function Message({
  createdBy,
  createdAt,
  files = [],
  children,
  onDownload,
  pendingFileId,
}: MessageProps) {
  const { me } = useSnapshot(store);

  const isMe = createdBy.id === me?.id;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const fileItems = useMemo(() => {
    if (!files.length) return null;

    return (
      <div className="flex w-full flex-wrap gap-2">
        {files.map((f) => (
          <MessageFile
            key={`message-file-item-${f.id}`}
            file={f}
            onDownload={onDownload}
            pending={pendingFileId === f.id}
          />
        ))}
      </div>
    );
  }, [files, pendingFileId]);

  if (isMe) {
    return (
      <div className="relative flex w-full justify-end">
        <div className="flex w-3/4 items-end space-x-2">
          <div className="relative flex grow flex-col items-start rounded border bg-grey-10 p-2 text-sm font-noto">
            <div className="flex w-full items-start justify-between">
              <div className="font-bold">{createdBy.name}</div>
              <MessageDate date={new Date(createdAt)} />
            </div>
            <div className="my-2 whitespace-pre-wrap">{children}</div>
            {fileItems}
          </div>

          <div>
            <Avatar me={createdBy} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex w-full justify-start">
      <div className="flex w-3/4 items-end space-x-2">
        <div>
          <Avatar me={createdBy} />
        </div>

        <div className="relative flex grow flex-col items-start rounded border bg-grey-10 p-2">
          <div className="flex w-full items-start justify-between">
            <div className="font-bold">{createdBy.name}</div>
            <MessageDate date={new Date(createdAt)} />
          </div>
          <div className="my-2 whitespace-pre-wrap">{children}</div>

          {fileItems}
        </div>
      </div>
    </div>
  );
}
